
'use client';

import { useState, useRef } from 'react';
import { analyzeInspection, type AnalyzeInspectionOutput } from '@/ai/flows/analyze-inspection';
import { analyzeCarSound, type AnalyzeSoundOutput } from '@/ai/flows/analyze-car-sound';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Camera,
  Loader2,
  CircleAlert,
  CircleCheck,
  ShieldAlert,
  Plus,
  X,
  Sparkles,
  Cpu,
  Image as ImageIcon,
  Mic,
  Square,
  Volume2,
  Trash2,
  Upload,
  Lightbulb,
  Info,
  Wrench,
  Calendar
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import Image from 'next/image';
import { getContextualTips } from '@/app/lib/tips-data';

export default function InspectionClient() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [symptomInput, setSymptomInput] = useState('');
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [result, setResult] = useState<AnalyzeInspectionOutput | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioResult, setAudioResult] = useState<AnalyzeSoundOutput | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // State for inline advice
  const [localAdvice, setLocalAdvice] = useState<string | null>(null);
  const [generatingAdvice, setGeneratingAdvice] = useState(false);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUri = reader.result as string;
        setImagePreview(dataUri);
        setPhotoUrl(dataUri);
      };
      reader.readAsDataURL(file);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks: BlobPart[] = [];
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      toast({ variant: 'destructive', title: 'صلاحية الميكروفون', description: 'يرجى السماح بالوصول للميكروفون.' });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioResult(null);
  };

  const addSymptom = () => {
    if (symptomInput.trim() && !symptoms.includes(symptomInput.trim())) {
      setSymptoms([...symptoms, symptomInput.trim()]);
      setSymptomInput('');
    }
  };

  const removeSymptom = (s: string) => {
    setSymptoms(symptoms.filter(item => item !== s));
  };

  const generateAdvice = () => {
    setGeneratingAdvice(true);
    setTimeout(() => {
      const tips = getContextualTips('inspection', 1);
      setLocalAdvice(tips[0].text_ar);
      setGeneratingAdvice(false);
    }, 800);
  };

  const handleAnalyze = async () => {
    if (!photoUrl && symptoms.length === 0 && !audioBlob) {
      toast({ variant: 'destructive', title: 'بيانات ناقصة', description: 'يرجى إدخال صورة، تسجيل صوتي، أو ذكر بعض الأعراض.' });
      return;
    }

    setLoading(true);
    try {
      if (photoUrl || symptoms.length > 0) {
        const output = await analyzeInspection({
          make_id: '1',
          model_id: '1',
          photo_url: photoUrl || 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=1000',
          symptoms: symptoms.length > 0 ? symptoms : ['فحص بصري'],
        });
        setResult(output);
      }

      if (audioBlob) {
        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
          const audioDataUri = reader.result as string;
          const soundOutput = await analyzeCarSound({ audioDataUri });
          setAudioResult(soundOutput);
        };
      }
      toast({ title: 'تم التحليل', description: 'تمت معالجة جميع البيانات بنجاح.' });
    } catch (err: any) {
      console.error(err);
      toast({ variant: 'destructive', title: 'خطأ في التحليل', description: 'حدث خطأ أثناء الاتصال بالذكاء الاصطناعي.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto pb-20">
      <Card className="rounded-[2.5rem] border-none shadow-xl overflow-hidden bg-white">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-primary text-white p-8">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-md">
              <Camera className="h-7 w-7" />
            </div>
            <div>
              <CardTitle className="text-xl font-black font-headline">الفحص البصري والصوتي</CardTitle>
              <p className="text-white/70 text-xs mt-1">حلل حالة سيارتك بالصور والرسائل الصوتية</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-8 space-y-6">
          <div className="space-y-6">
            <div className="space-y-3">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-primary" /> صورة الجزء المراد فحصه
              </Label>
              <div className="flex flex-col gap-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="رابط الصورة أو اختر ملفاً..."
                    value={photoUrl.startsWith('data:') ? 'تم اختيار صورة' : photoUrl}
                    onChange={(e) => setPhotoUrl(e.target.value)}
                    className="rounded-2xl h-14 bg-slate-50 border-slate-200"
                  />
                  <div className="relative">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="absolute inset-0 opacity-0 cursor-pointer w-14 h-14" />
                    <Button variant="outline" className="h-14 w-14 rounded-2xl" size="icon"><Upload className="h-5 w-5" /></Button>
                  </div>
                </div>
                {imagePreview && (
                  <div className="relative w-full aspect-video rounded-3xl overflow-hidden border">
                    <Image src={imagePreview} alt="Preview" fill className="object-cover" />
                    <Button variant="destructive" size="icon" className="absolute top-2 left-2 rounded-full h-8 w-8" onClick={() => { setImagePreview(null); setPhotoUrl(''); }}><X className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <Mic className="h-4 w-4 text-orange-500" /> سجل صوت العطل أو تحدث لنا
              </Label>
              <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-[2rem] border shadow-inner">
                {!audioUrl ? (
                  <Button className={`flex-1 h-14 rounded-2xl font-bold transition-all ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`} onClick={isRecording ? stopRecording : startRecording}>
                    {isRecording ? <><Square className="h-4 w-4 ml-2" /> إيقاف</> : <><Mic className="h-4 w-4 ml-2" /> ابدأ التسجيل</>}
                  </Button>
                ) : (
                  <div className="flex items-center gap-3 w-full">
                    <audio src={audioUrl} controls className="flex-1 h-10" />
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={deleteRecording}><Trash2 className="h-5 w-5" /></Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-bold text-slate-700 flex items-center gap-2"><Sparkles className="h-4 w-4 text-indigo-500" /> وصف إضافي</Label>
              <div className="flex gap-2">
                <Input placeholder="مثلاً: بقع زيت، صرير..." value={symptomInput} onChange={(e) => setSymptomInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && addSymptom()} className="rounded-2xl h-14 bg-slate-50" />
                <Button onClick={addSymptom} className="rounded-2xl h-14 w-14 bg-slate-900 text-white" size="icon"><Plus className="h-6 w-6" /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {symptoms.map(s => (
                  <Badge key={s} className="bg-slate-100 text-slate-700 px-4 py-1.5 rounded-full flex items-center gap-2 font-bold">
                    {s}<X className="h-3 w-3 cursor-pointer" onClick={() => removeSymptom(s)} />
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          <Button className="w-full h-16 rounded-[2rem] text-lg font-black bg-primary hover:bg-primary/90 shadow-xl transition-all" onClick={handleAnalyze} disabled={loading}>
            {loading ? <div className="flex items-center gap-3"><Loader2 className="animate-spin h-6 w-6" /> جاري التحليل...</div> : <div className="flex items-center gap-2"><Sparkles className="h-6 w-6" /> حلل الآن</div>}
          </Button>
        </CardContent>
      </Card>

      {/* Interactive Advice Button */}
      <div className="space-y-4">
        <Button
          variant="outline"
          className="w-full h-14 rounded-2xl border-dashed border-primary text-primary flex gap-2 font-bold hover:bg-blue-50 bg-white shadow-sm"
          onClick={generateAdvice}
          disabled={generatingAdvice}
        >
          {generatingAdvice ? <Loader2 className="animate-spin h-4 w-4" /> : <Lightbulb className="h-4 w-4" />}
          بماذا تنصحني؟
        </Button>

        {localAdvice && (
          <div className="p-5 bg-blue-50 border border-blue-100 rounded-[2.5rem] animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="flex items-center gap-2 mb-2 text-primary">
              <Sparkles className="h-4 w-4" />
              <h4 className="text-xs font-black uppercase">نصيحة الخبير الذكية:</h4>
            </div>
            <p className="text-sm font-bold text-slate-700 leading-relaxed">{localAdvice}</p>
            <div className="mt-3 pt-3 border-t border-blue-100 flex items-center gap-2">
              <Info className="h-3 w-3 text-primary/40" />
              <p className="text-[10px] text-primary/40 font-bold uppercase tracking-tight">مقارنة: الفحص الصوتي يكتشف 60% من أعطال المحرك المبكرة.</p>
            </div>
          </div>
        )}
      </div>

      {audioResult && (
        <Card className="rounded-[2.5rem] border-none bg-orange-50 text-orange-900 shadow-md">
          <CardHeader className="pb-2"><div className="flex items-center gap-2"><Volume2 className="h-5 w-5" /><h4 className="font-black">تحليل الصوت</h4></div></CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-white/50 p-4 rounded-2xl"><p className="text-[10px] font-bold opacity-60 mb-1">الوصف</p><p className="text-sm font-medium">{audioResult.soundDescription}</p></div>
            <div className="space-y-2"><p className="text-[10px] font-bold opacity-60 mb-1">الأعطال</p><div className="flex flex-wrap gap-2">{audioResult.possibleFaults.map((f, i) => (<Badge key={i} className="bg-orange-200 text-orange-800 rounded-lg">{f}</Badge>))}</div></div>
            <div className="bg-orange-600 text-white p-4 rounded-2xl shadow-lg"><p className="text-[10px] font-bold opacity-80 mb-1">التوصية</p><p className="text-sm font-bold">{audioResult.recommendation}</p></div>
          </CardContent>
        </Card>
      )}

      {result && (
        <div className="space-y-4">
          {(result.estimated_repair_range || result.workshop_booking_recommended) && (
            <Card className="rounded-[2.5rem] border-none bg-blue-50 text-blue-900 shadow-md animate-in fade-in slide-in-from-bottom-4 duration-500">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 text-blue-700">
                  <Wrench className="h-5 w-5" />
                  <h4 className="font-black text-lg">توصيات الإصلاح</h4>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {result.estimated_repair_range && (
                  <div className="mb-4 bg-white/60 p-4 rounded-2xl">
                    <p className="text-[10px] font-bold opacity-60 mb-1">التكلفة التقديرية للإصلاح</p>
                    <p className="text-xl font-black text-primary">{result.estimated_repair_range}</p>
                  </div>
                )}
                {result.workshop_booking_recommended && (
                  <Button className="w-full h-14 rounded-2xl gap-2 font-bold shadow-lg bg-blue-600 hover:bg-blue-700 text-white" onClick={() => window.location.href = '/services'}>
                    <Calendar className="w-5 h-5" />
                    حجز موعد ورشة الآن
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {result.results.map((res, idx) => (
            <Card key={idx} className="rounded-[2.5rem] border-none shadow-md overflow-hidden bg-white">
              <div className={`h-2 ${res.risk_level === 'critical' ? 'bg-red-500' : res.risk_level === 'high' ? 'bg-orange-500' : 'bg-yellow-500'}`} />
              <CardContent className="p-8">
                <div className="flex justify-between items-start mb-4"><h4 className="text-xl font-black text-slate-800 font-headline">{res.issue_title_ar}</h4><Badge variant="secondary" className="rounded-full bg-slate-50 text-slate-500 text-[10px]">ثقة {res.confidence}%</Badge></div>
                <div className="space-y-6">
                  <div className="bg-slate-50 p-5 rounded-2xl"><p className="text-[11px] font-bold text-slate-400 mb-2">التأثير المتوقع</p><p className="text-slate-700 text-sm">{res.impact_ar}</p></div>
                  <div className="space-y-3"><p className="text-[11px] font-bold text-slate-400 px-1">الإجراءات</p><div className="grid grid-cols-1 gap-2">{res.actions_ar.map((action, i) => (<div key={i} className="flex items-center gap-3 text-xs text-slate-600 bg-white p-4 rounded-2xl border">{action}</div>))}</div></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
